import React, { useState } from "react";
import { Star, X } from "lucide-react";
import Toast from "../common/Toast";
import { useCreateCourseFeedbackMutation } from "../../services/course";

const getFeedbackErrorMessage = (error) => {
  const data = error?.data;
  if (typeof data === "string") return data;
  if (data && typeof data.message === "string") return data.message;
  if (typeof error?.error === "string") return error.error;
  return "Failed to submit feedback. Please try again.";
};

const CourseFeedbackModal = ({ open, onClose, courseId }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [ratingError, setRatingError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "default",
  });
  const [createCourseFeedback, { isLoading }] = useCreateCourseFeedbackMutation();

  if (!open) return null;

  const resetForm = () => {
    setRating(0);
    setHoveredRating(0);
    setFeedback("");
    setFeedbackError("");
    setRatingError("");
    setSubmitError("");
  };

  const validateFeedback = (value) => {
    const trimmed = value.trim();

    if (!trimmed) {
      return "Please share your thoughts about the course.";
    }

    if (trimmed.length < 10 || trimmed.length > 2000) {
      return "Feedback text must be between 10 and 2000 characters";
    }

    return "";
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!courseId) return;

    setSubmitError("");

    if (!rating) {
      setRatingError("Please select a star rating before submitting.");
      return;
    }

    setRatingError("");

    const validationError = validateFeedback(feedback);
    if (validationError) {
      setFeedbackError(validationError);
      return;
    }

    setFeedbackError("");
    const trimmedFeedback = feedback.trim();

    try {
      const result = await createCourseFeedback({
        courseId,
        feedbackText: trimmedFeedback,
        rating,
      }).unwrap();

      if (result && typeof result === "object" && result.success === false) {
        setSubmitError(
          typeof result.message === "string"
            ? result.message
            : "Could not submit your feedback.",
        );
        return;
      }

      setToast({
        show: true,
        title: "Feedback submitted",
        message: "Thank you for sharing your experience.",
        variant: "success",
      });
      handleClose();
    } catch (error) {
      setSubmitError(getFeedbackErrorMessage(error));
    }
  };

  const activeRating = hoveredRating || rating;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-[917px] rounded-[50px] bg-white pt-11 pb-[50px] shadow-[0_0_3.7px_4px_#00000040] font-['Roboto']">
          <div className="flex items-start justify-between border-b border-[#00000080] pb-6 pl-[107px] pr-[98px]">
            <h2 className="text-4xl font-medium text-black">Course Feedback</h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close course feedback"
              className="text-black transition-opacity hover:opacity-70"
            >
              <X size={24} />
            </button>
          </div>

          <div className="mt-16 pl-[107px] pr-[98px]">
            <p className="text-xl font-medium leading-[23px] text-black">
              How has your experience been with this course?
            </p>

            <div className="mt-[26px] flex items-center justify-end gap-[14px]">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setRating((current) => (current === value ? 0 : value));
                    if (ratingError) {
                      setRatingError("");
                    }
                    if (submitError) {
                      setSubmitError("");
                    }
                  }}
                  onMouseEnter={() => setHoveredRating(value)}
                  onMouseLeave={() => setHoveredRating(0)}
                  aria-label={`Rate ${value} star${value > 1 ? "s" : ""}`}
                  className="transition-transform hover:scale-105"
                >
                  <Star
                    size={40}
                    className={
                      value <= activeRating
                        ? "fill-[#FF6636] text-[#FF6636]"
                        : ratingError
                          ? "text-[#DC2626]"
                          : "text-black"
                    }
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            {ratingError ? (
              <p className="mt-2 text-right text-sm text-[#DC2626]">
                {ratingError}
              </p>
            ) : null}

            <textarea
              value={feedback}
              onChange={(event) => {
                const value = event.target.value;
                setFeedback(value);
                if (feedbackError) {
                  setFeedbackError(validateFeedback(value));
                }
                if (submitError) {
                  setSubmitError("");
                }
              }}
              onBlur={() => {
                if (feedback.trim()) {
                  setFeedbackError(validateFeedback(feedback));
                }
              }}
              maxLength={2000}
              placeholder="Share your thought about the course content, instructor, pace or any suggestions for improvement...."
              className={`mt-6 min-h-[179px] w-full resize-none border px-5 py-7 font-normal text-xl text-black outline-none placeholder:text-[#938A96] leading-[19px] ${
                feedbackError ? "border-[#DC2626]" : "border-black"
              }`}
            />

            <div className="mt-2 flex items-start justify-between gap-3">
              {feedbackError ? (
                <p className="text-sm text-[#DC2626]">{feedbackError}</p>
              ) : (
                <span />
              )}
              <p className="shrink-0 text-sm text-[#938A96]">
                {feedback.trim().length}/2000
              </p>
            </div>

            {submitError ? (
              <p className="mt-4 text-sm text-[#DC2626]">{submitError}</p>
            ) : null}

            <div className="mt-9 flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="rounded-lg bg-[#FF6636] px-[75px] py-[18px] text-2xl leading-7 font-medium text-white transition-colors hover:bg-[#e65a2e] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {toast.show ? (
        <Toast
          title={toast.title}
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast((current) => ({ ...current, show: false }))}
        />
      ) : null}
    </>
  );
};

export default CourseFeedbackModal;
